/**
 * Process / View Anatomy
 * 
 * **Model 3 (Multiple Views per Process) is the authoritative pattern for all processes.**
 * 
 * Provides a topology-agnostic system where processes can have zero, one, or many visual instances.
 * A process is the fundamental unit of functionality in Pivot.is - it can be headless or have UI.
 * 
 * **Standard Pattern (Model 3):**
 * - One process can spawn zero, one, or many views
 * - Views are managed independently via `ctx.registerView()` and `ctx.destroyView()`
 * - Process lifecycle is independent of individual view lifecycle
 * - Processes can dynamically spawn/destroy views (like Electron main process + windows)
 * 
 * This enables:
 * - 2D cards (squares) - one view = one square
 * - Multi-square views (e.g., calendar with 7 squares for weekly layout)
 * - Full viewport views (e.g., game taking entire screen)
 * - Stacked flashcards
 * - Layered shaders
 * - Voronoi particle fields
 * - Headless logic modules (background services, data processors, etc.)
 * - Multi-window processes (e.g., chat with friends list + multiple conversation windows)
 * 
 * **Views are NOT one-to-one with squares:**
 * - A view can render a single square
 * - A view can render multiple squares (e.g., calendar grid)
 * - A view can take over the entire viewport (e.g., full-screen game)
 * - The view's `render()` method controls what is rendered within its geometry bounds
 * 
 * This makes Pivot.is topology-agnostic: a chat process, shader field, or
 * 3D Voronoi simulation all participate in the same signal fabric, even
 * when they have no visible "squares."
 * 
 * ALL processes in the system must:
 * 1. Implement the Process interface (Model 3 pattern)
 * 2. Register via `registerProcess(kind, factory)` in the systems registry
 * 3. Use `ctx.registerView()` to spawn views (can be called multiple times)
 * 4. Use `ctx.destroyView(viewId)` to close individual views
 * 
 * There are no disconnected chunks of functionality - everything uses the Process interface.
 * 
 * @see {@link docs/PROCESS_UI_MODELS.md} for detailed Model 3 documentation
 * @see {@link docs/MODEL3_MIGRATION_BREAKING_PATTERNS.md} for migration guide
 */

import { EventBus, bus } from '../core/events.js';
import type { PivotEventMap } from '../types/events.js';
import { log, trace } from '../services/logging/index.js';
import { viewManager } from './view-manager.js';
import { GridPositionPreference } from '../grid-allocation-v2/types.js';

/**
 * Process lifecycle mode - determines behavior when views close
 */
export enum ProcessLifecycleMode {
  /** Stop process when all views are closed (default for UI processes) */
  STOP_ON_ALL_VIEWS_CLOSE = 'stop-on-all-views-close',

  /** Keep process running when views close (for background/headless processes) */
  KEEP_ALIVE = 'keep-alive',

  /** Stop process when any view closes (strict 1:1 coupling) */
  STOP_ON_ANY_VIEW_CLOSE = 'stop-on-any-view-close'
}

/**
 * Process context provided to processes when they start
 * 
 * **Model 3 Pattern (Authoritative):**
 * A process can spawn zero, one, or many views via `ctx.registerView()`.
 * 
 * Standard usage:
 * - Zero views = headless process (background service)
 * - One view = UI process with single view (still uses Model 3 pattern)
 * - Multiple views = multi-window process (like Electron main process + windows)
 * 
 * Examples:
 * - Chat process: `ctx.registerView(friendsListView)` + `ctx.registerView(chatWindowView)` + `ctx.registerView(settingsView)`
 * - Todo process: `ctx.registerView(mainView)` (Model 3, just one view)
 * - Analytics process: Initially zero views, `ctx.registerView(dashboardView)` when user requests UI
 * 
 * Views can be spawned/destroyed dynamically:
 * ```typescript
 * // Spawn new view later
 * ctx.registerView(newView);
 * 
 * // Close specific view
 * ctx.destroyView(viewId);
 * ```
 */
export interface ProcessContext {
  /** Unique identifier for this process instance */
  id: string;

  /** Isolated state domain for this process */
  state: Record<string, unknown>;

  /** EventBus for process communication */
  bus: EventBus<PivotEventMap>;

  /** 
   * Register a view (can be called multiple times to spawn multiple views)
   * 
   * Example: Chat process might register:
   * - Friends list view
   * - Chat window view
   * - Settings panel view
   */
  registerView(view: ViewInstance): void;

  /**
   * Update existing view content (replace content instead of spawning new view)
   * 
   * Example: Friends list view → conversation view (same view, different content)
   */
  updateView(viewId: string, newContent: unknown): void;

  /** 
   * Destroy a view by ID
   * 
   * The process lifecycle mode determines what happens when views close:
   * - STOP_ON_ALL_VIEWS_CLOSE: Process stops when last view closes
   * - KEEP_ALIVE: Process continues running even when all views close
   * - STOP_ON_ANY_VIEW_CLOSE: Process stops when any view closes
   */
  destroyView(viewId: string): void;

  /** Get all views currently registered for this process */
  getViews?(): ViewInstance[];

  /** Process lifecycle mode (defaults to STOP_ON_ALL_VIEWS_CLOSE) */
  lifecycleMode?: ProcessLifecycleMode;

  /** Track an event listener for cleanup (automatically cleaned up on process stop) */
  trackEventListener?(event: string, handler: (payload: unknown) => void): void;

  /** Track a timer for cleanup (automatically cleaned up on process stop) */
  trackTimer?(timerId: number): void;

  /** Track an interval for cleanup (automatically cleaned up on process stop) */
  trackInterval?(intervalId: number): void;
}

/**
 * ProcessContext implementation
 * 
 * Creates a ProcessContext instance for a process.
 * This is the concrete implementation that processes receive when they start.
 * 
 * Usage:
 * ```typescript
 * const ctx = createProcessContext(processId, bus, lifecycleMode, initialState);
 * ctx.setProcess(process);
 * process.start(ctx);
 * ```
 */
export class ProcessContextImpl implements ProcessContext {
  public readonly id: string;
  public readonly state: Record<string, unknown>;
  public readonly bus: EventBus<PivotEventMap>;
  public readonly lifecycleMode: ProcessLifecycleMode;

  private views: Map<string, ViewInstance> = new Map();
  private process: Process | null = null;

  // CRITICAL: Track resources for cleanup on process destruction
  private eventListeners: Array<{ event: string; handler: (payload: unknown) => void }> = [];
  private timers: Set<number> = new Set(); // setInterval/setTimeout IDs
  private intervals: Set<number> = new Set(); // setInterval IDs only

  constructor(
    id: string,
    eventBus: EventBus<PivotEventMap> = bus,
    lifecycleMode: ProcessLifecycleMode = ProcessLifecycleMode.STOP_ON_ALL_VIEWS_CLOSE,
    initialState: Record<string, unknown> = {}
  ) {
    this.id = id;
    this.bus = eventBus;
    this.lifecycleMode = lifecycleMode;
    this.state = initialState;
  }

  /**
   * Track an event listener for cleanup
   */
  trackEventListener(event: string, handler: (payload: unknown) => void): void {
    this.eventListeners.push({ event, handler });
    this.bus.on(event as any, handler);
  }

  /**
   * Track a timer for cleanup
   */
  trackTimer(timerId: number): void {
    this.timers.add(timerId);
  }

  /**
   * Track an interval for cleanup
   */
  trackInterval(intervalId: number): void {
    this.intervals.add(intervalId);
    this.timers.add(intervalId);
  }

  /**
   * Cleanup all tracked resources (event listeners, timers, intervals)
   * Called automatically on process stop
   */
  cleanup(): void {
    // Remove all event listeners
    for (const { event, handler } of this.eventListeners) {
      this.bus.off(event as any, handler);
    }
    this.eventListeners = [];

    // Clear all timers and intervals
    for (const timerId of this.timers) {
      if (this.intervals.has(timerId)) {
        window.clearInterval(timerId);
      } else {
        window.clearTimeout(timerId);
      }
    }
    this.timers.clear();
    this.intervals.clear();
  }

  registerView(view: ViewInstance): void {
    const traceId = trace.start('process-context-register-view');

    if (this.views.has(view.id)) {
      trace.end(traceId);
      return;
    }

    // Set process ID on view
    view.processId = this.id;

    // Register view
    this.views.set(view.id, view);

    // CRITICAL: Register view directly with ViewManager
    // ViewManager no longer listens to process.view.registered events
    // Views must be registered directly via registerView()
    viewManager.registerView(view);

    // Emit view registered event (for other systems that might listen)
    this.bus.emit('process.view.registered', {
      processId: this.id,
      viewId: view.id,
      view: view
    });

    // Notify process of viewport change
    if (this.process?.onViewportChange) {
      this.process.onViewportChange(view.id, 'mounted');
    } else {
    }

    trace.end(traceId);
  }

  updateView(viewId: string, newContent: unknown): void {
    const traceId = trace.start('process-context-update-view');

    const view = this.views.get(viewId);
    if (!view) {
      trace.end(traceId);
      return;
    }

    // Update view data/content
    view.data = newContent;

    // Emit view updated event
    this.bus.emit('process.view.updated', {
      processId: this.id,
      viewId: viewId,
      content: newContent
    });

    trace.end(traceId);
  }

  destroyView(viewId: string): void {
    const traceId = trace.start('process-context-destroy-view');

    const view = this.views.get(viewId);
    if (!view) {
      trace.end(traceId);
      return;
    }

    const remainingViews = this.views.size - 1;

    // Call view destroy hook
    if (view.destroy) {
      view.destroy();
    }

    // Remove view
    this.views.delete(viewId);

    // Emit view destroyed event
    this.bus.emit('process.view.destroyed', {
      processId: this.id,
      viewId: viewId
    });

    // Notify process of viewport change
    if (this.process?.onViewportChange) {
      this.process.onViewportChange(viewId, 'unmounted');
    }

    // Check lifecycle mode and handle process lifecycle
    this.checkLifecycle();

    trace.end(traceId);
  }

  getViews(): ViewInstance[] {
    return Array.from(this.views.values());
  }

  /**
   * Check process lifecycle based on lifecycle mode
   */
  private checkLifecycle(): void {
    const viewCount = this.views.size;

    switch (this.lifecycleMode) {
      case ProcessLifecycleMode.STOP_ON_ALL_VIEWS_CLOSE:
        if (viewCount === 0 && this.process) {
          // Emit event to stop process through ProcessManager
          // This ensures proper cleanup including removal from persisted state
          this.bus.emit('process.stop', { processId: this.id });
        }
        break;

      case ProcessLifecycleMode.STOP_ON_ANY_VIEW_CLOSE:
        // Stop process when any view closes (strict 1:1 coupling)
        if (this.process) {
          // Emit event to stop process through ProcessManager
          // This ensures proper cleanup including removal from persisted state
          this.bus.emit('process.stop', { processId: this.id });
        }
        break;

      case ProcessLifecycleMode.KEEP_ALIVE:
        // Process continues running regardless of view count
        break;
    }
  }

  /**
   * Set the process instance (called after process is created)
   * This allows the context to notify the process of viewport changes
   */
  setProcess(process: Process): void {
    this.process = process;
  }
}

/**
 * Create a ProcessContext instance for a process
 * 
 * This is the factory function to create ProcessContext instances.
 * The process should be set via `ctx.setProcess(process)` before starting.
 */
export function createProcessContext(
  id: string,
  eventBus: EventBus<PivotEventMap> = bus,
  lifecycleMode: ProcessLifecycleMode = ProcessLifecycleMode.STOP_ON_ALL_VIEWS_CLOSE,
  initialState: Record<string, unknown> = {}
): ProcessContextImpl {
  return new ProcessContextImpl(id, eventBus, lifecycleMode, initialState);
}

/**
 * Process interface
 * 
 * A process is the fundamental unit of functionality in Pivot.is.
 * 
 * - A process can be headless (no UI) - e.g., background data processor, analytics service
 * - A process can have UI - e.g., todo list, chat, debugger, AI-floated content
 * - A process can have zero, one, or many visual instances (views)
 * 
 * **Model 3 (1:0..N) is the authoritative pattern for all processes.**
 * 
 * Model 3 Pattern:
 * - Process can spawn zero, one, or many views dynamically
 * - Views are managed independently (spawn/close individual views)
 * - Process lifecycle independent of view lifecycle
 * - Like Electron: main process can spawn multiple windows
 * 
 * Examples:
 * - Chat process: friends list view + chat window view + settings panel (multiple views)
 * - Todo process: one main view (still Model 3, just spawns one view)
 * - Analytics process: zero views initially, spawns dashboard view on demand (headless → UI)
 * 
 * The Process interface natively supports Model 3. All processes should use this pattern.
 * Processes can choose to spawn 0, 1, or N views based on their needs.
 * 
 * ALL processes must:
 * 1. Implement this interface
 * 2. Register via `registerProcess(kind, factory)` in the systems registry
 * 3. Be created via `createProcess(kind, id)` when needed
 * 
 * Processes are started with a ProcessContext that provides:
 * - State domain (isolated state for this process)
 * - EventBus access (for communication)
 * - View registration (to create visual instances)
 */
export interface Process {
  /** Unique identifier for this process instance */
  id: string;

  /** Process kind/type (e.g., 'debugger', 'todo', 'chat', 'demo-1') */
  kind: string;

  /** Process metadata (priority, lifecycle, allocation behavior) */
  metadata?: ProcessMetadata;

  /** Start the process with the provided context */
  start(ctx: ProcessContext): void;

  /** Stop the process (optional - for cleanup) */
  stop?(): void;

  /** Update the process each frame (optional - for animation/continuous updates) */
  update?(dt: number): void;

  /** Handle incoming signals/events (optional - for event-driven logic) */
  onSignal?(event: string, payload: unknown): void;

  /**
   * Handle viewport allocation changes
   * Called when view allocation changes (e.g., view unmounted, collapsed, expanded)
   */
  onViewportChange?(viewId: string, change: 'mounted' | 'unmounted' | 'collapsed' | 'expanded'): void;

  /**
   * Save process state for preservation
   * Called before unmounting if metadata.preserveState is true
   */
  saveState?(): Record<string, unknown>;

  /**
   * Restore process state from preservation
   * Called when remounting if state was preserved
   */
  restoreState?(state: Record<string, unknown>): void;
}

/**
 * Process priority levels
 * 
 * Determines viewport allocation priority and interruptibility.
 * Lower priority processes yield to higher priority processes.
 */
export enum ProcessPriority {
  /** System-critical (cannot be dismissed/unmounted) */
  SYSTEM = 1000,

  /** Time-sensitive (video calls, alarms, critical notifications) */
  CRITICAL = 900,

  /** High priority (important messages, LLM responses) */
  HIGH = 700,

  /** Normal priority (most processes) */
  NORMAL = 500,

  /** Low priority (background utilities) */
  LOW = 300,

  /** Trivial (can always be dismissed/collapsed, e.g., notifications) */
  TRIVIAL = 100
}

/**
 * Layout modes for simple, deterministic layouts
 * 
 * These bypass the complex allocation algorithm for well-known patterns.
 */
export enum LayoutMode {
  /** Default - use complex allocation algorithm */
  AUTO = 'auto',
  
  /** To-Do List: Single column view (AI can spawn multiple) */
  TODO_LIST = 'todo-list',
  
  /** Eisenhower: 2x2 grid, centered */
  EISENHOWER = 'eisenhower',
  
  /** Calendar: 7x5 grid (7 columns x 5 rows) */
  CALENDAR = 'calendar',
  
  /** Horizontal row: Views arranged left-to-right */
  HORIZONTAL_ROW = 'horizontal-row',
  
  /** Vertical column: Views arranged top-to-bottom */
  VERTICAL_COLUMN = 'vertical-column',
  
  /** Grid: NxM grid (specified in layoutConfig) */
  GRID = 'grid'
}

/**
 * Process metadata
 * 
 * Defines priority, lifecycle, and allocation behavior for a process.
 */
export interface ProcessMetadata {
  /** Process priority level */
  priority: ProcessPriority;

  /** Process category (affects allocation behavior) */
  category?: 'system' | 'communication' | 'productivity' | 'entertainment' | 'utility' | 'demo';

  /** Can process be stopped/destroyed? */
  canStop: boolean;

  /** Should process preserve state when unmounted? */
  preserveState: boolean;

  /** Time-sensitive? (can interrupt lower priority) */
  timeSensitive: boolean;

  /** Maximum viewport allocation (percentage or fixed) */
  maxViewportAllocation?: number | { width: number; height: number };
  
  /** Layout mode for simple, deterministic layouts (bypasses complex allocation) */
  layoutMode?: LayoutMode;
  
  /** Layout configuration (for GRID mode: { cols: number, rows: number }) */
  layoutConfig?: { cols?: number; rows?: number; [key: string]: unknown };
}

/**
 * View size constraints (v2 schema - cell-based)
 * 
 * Uses cell-based acceptableSizes array:
 * - First entry = preferred/expanded size (or null for unlimited)
 * - Second entry = normal size (optional)
 * - Last entry = minimum size
 */
export interface ViewSizeConstraints {
  /** Acceptable sizes (tried in order) - cell-based */
  acceptableSizes: Array<{ cols: number; rows: number } | null>;
}

/**
 * View metadata (v2 schema)
 * 
 * Defines priority, position, size, and page restrictions for a view.
 */
export interface ViewMetadata {
  /** View priority (higher = allocated first, default: 1) */
  priority?: number;

  /** Preferred position (semantic, viewport-independent) */
  preferredPosition?: GridPositionPreference;

  /** Size constraints (cell-based acceptableSizes) */
  sizeConstraints: ViewSizeConstraints;

  /** Page restrictions (which pages this view can be on) */
  allowedPages?: number[];

  /** Show dismiss button in top-right corner? */
  showDismissButton?: boolean;

  /** Height mode: 'full' = full viewport height, 'half' = half height (can stack) */
  heightMode?: 'full' | 'half';

  /** Width mode: 'standard' = default width, 'double' = 2x default width */
  widthMode?: 'standard' | 'double';

  /** Is this view an overlay? (Skipped by layout allocator, fixed position) */
  isOverlay?: boolean;
}

/**
 * View instance geometry
 * 
 * Defines the spatial representation of a view.
 * 
 * **Note:** Geometry can be overridden by viewport allocation manager
 * based on priority, policies, and available space.
 */
export interface ViewGeometry {
  x: number;
  y: number;
  width?: number;
  height?: number;
  z?: number;
  rotation?: number;
  scale?: number;
  [key: string]: unknown;
}

/**
 * View instance
 * 
 * A visual representation that can be rendered on canvas or WebGL.
 * 
 * **Important: Views are NOT one-to-one with squares.**
 * 
 * A view can represent:
 * - A single square (simple case)
 * - Multiple squares (e.g., calendar view with 7 squares for weekly layout)
 * - The entire viewport (e.g., full-screen game view)
 * - A layer/stack of elements (e.g., notification stack)
 * - Custom rendering (e.g., WebGL shader effects)
 * 
 * The `geometry` property defines the spatial bounds of the view.
 * The `render()` method is responsible for rendering all visual elements
 * within those bounds (including multiple squares if needed).
 * 
 * Examples:
 * - Calendar process: One view that renders 7 squares (one per day of week)
 * - Game process: One view that takes over entire viewport
 * - Todo process: One view that renders a single square
 * - Chat process: Multiple views (friends list view, chat window view, etc.)
 */
export interface ViewInstance {
  id: string;
  kind: 'square' | 'layer' | 'stack' | 'particle' | 'canvas' | 'none';
  geometry?: ViewGeometry;
  data?: unknown;

  /** 
   * View metadata (lifecycle, allocation, priority behavior)
   * 
   * Defines how this view behaves in viewport allocation:
   * - Can it be unmounted? (lifecyclePolicy, canUnmount)
   * - How is space allocated? (allocationPolicy)
   * - What's its priority? (priority override)
   * - Can it be collapsed? (canCollapse, collapsedSize)
   */
  metadata?: ViewMetadata;

  /** 
   * Process ID that owns this view
   * Used for process-level priority and lifecycle management
   */
  processId?: string;

  /** Hidden flag (set by viewport allocation manager when unmounted) */
  hidden?: boolean;

  /** Collapsed flag (set by viewport allocation manager when collapsed) */
  collapsed?: boolean;

  /** Expanded flag (set by view or process - indicates if view is in expanded state) */
  expanded?: boolean;

  /** 
   * Render the view (can render multiple squares or full viewport)
   * 
   * This method is responsible for rendering all visual elements
   * within the view's geometry bounds, which may include:
   * - One square
   * - Multiple squares (e.g., calendar grid)
   * - Full viewport (e.g., game)
   * - Custom rendering (e.g., WebGL effects)
   * 
   * **Note:** Geometry may be modified by viewport allocation manager
   * based on priority, available space, and lifecycle policies.
   */
  render?(ctx: CanvasRenderingContext2D | WebGLRenderingContext): void;

  destroy(): void;
}

